import * as React from 'react';
import styled, { useTheme } from 'styled-components';
import StyledSvg from '@sdk/components/ui/StyledSvg';
import BoxIcon from '@sdk/shifts/components/BoxIcon';
import EventBoxInner from '@sdk/schedule/components/EventBoxInner';
import EventBoxOuter from '@sdk/schedule/components/EventBoxOuter';
import EventBoxContainerStyle, {
  EventBoxContainerProps,
} from '@sdk/schedule/components/EventBoxContainerStyle';
import type { TimeOffBoxType } from '@sdk/timeOffs/types/TimeOffType';
import type { EventBoxCommonBaseProps } from '@sdk/schedule/types/EventBoxProps';

import NoteIconWhite from '../assets/svg/Icons/16/shift-note-white.svg';
import TimeOffIcon from '../assets/svg/Icons/24/shift-timeoff-white.svg';

import type { OpenEventCallback } from './types';
import FlexRow from './ui/FlexRow';
import EventBoxText from './components/EventBoxText';
import { EVENT_TEXT_LINE_HEIGHT } from './windowed/constants';

const Box = styled(FlexRow)<EventBoxContainerProps>`
  ${EventBoxContainerStyle}

  background-color: ${({ theme }): string => theme.colors.timeOff};
  border: 2px solid ${({ theme }): string => theme.colors.timeOff};
  opacity: ${({ isPast }): number => (isPast ? 0.8 : 1)};
`;

const SpaceBetweenRow = styled(FlexRow)`
  align-items: center;
  justify-content: space-between;
`;

const IconContainer = styled.div`
  height: ${EVENT_TEXT_LINE_HEIGHT}px;
`;

type Props = {
  timeOff: TimeOffBoxType;
  openEvent: OpenEventCallback;
} & EventBoxCommonBaseProps;

const TimeOffBox: React.FC<React.PropsWithChildren<Props>> = ({
  hoverComponent,
  timeOff,
  openEvent,
  shiftAppearanceMode,
  height,
  showPlus,
}) => {
  const { colors } = useTheme();
  const { week } = timeOff.shiftAppearance;
  const shouldDisplayThirdLine = shiftAppearanceMode === 'month' && week.thirdLine;

  const onClick = (evt: React.MouseEvent<HTMLDivElement>): void => {
    openEvent({ event: timeOff, isExchange: false, $event: evt });
  };

  return (
    <EventBoxOuter>
      <Box
        hoverComponent={hoverComponent}
        showPlus={showPlus}
        onClick={onClick}
        height={height}
        isPast={timeOff.isPast}>
        <EventBoxInner>
          <EventBoxText bold>{week.firstLine}</EventBoxText>

          <SpaceBetweenRow>
            {week.secondLine && <EventBoxText>{week.secondLine}</EventBoxText>}
            {!timeOff.showTimeoffIcon && timeOff.summary && (
              <IconContainer>
                <StyledSvg svg={NoteIconWhite} color={colors.white} />
              </IconContainer>
            )}
          </SpaceBetweenRow>

          {shouldDisplayThirdLine && <EventBoxText>{week.thirdLine}</EventBoxText>}

          {week.fourthLine && <EventBoxText>{week.fourthLine}</EventBoxText>}
        </EventBoxInner>
        {timeOff.showTimeoffIcon && <BoxIcon source={TimeOffIcon} color={colors.white} />}
      </Box>
    </EventBoxOuter>
  );
};

export default TimeOffBox;
